import { DomSanitizer } from '@angular/platform-browser';
import { catchError, map, mapTo, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { MessageService } from "../../core/message/message.service";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { EMPTY, Observable, throwError } from "rxjs";
import { GetFileData, GetFileInputData } from "./resource.model";

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean;
  }
}

@Injectable({
  providedIn: "root",
})
export class ResourceService {
  constructor(
    private messageService: MessageService,
    private http: HttpClient,
    private domSanitizer: DomSanitizer
  ) { }

  getFileData(inputData: GetFileInputData): Observable<GetFileData> {
    return this.toFileData(
      this.http.get(inputData.url, { responseType: "blob", observe: "response", params: inputData.params, headers: { hideLoader: inputData.hideLoader ? "true" : "" } }),
      inputData
    );
  }

  // POST that triggers a side-effecting server operation (e.g. generate a document / update the DB) and streams
  // the resulting file back. Named to contrast with getFileData — it "posts for" a file, it does not upload one.
  postForFileData(inputData: GetFileInputData, body: unknown = null): Observable<GetFileData> {
    return this.toFileData(
      this.http.post(inputData.url, body, { responseType: "blob", observe: "response", params: inputData.params, headers: { hideLoader: inputData.hideLoader ? "true" : "" } }),
      inputData
    );
  }

  download(inputData: GetFileInputData): Observable<void> {
    return this.getFileData(inputData).pipe(
      tap((data: GetFileData) => {
        if (navigator.msSaveBlob)
          // IE hack; see http://msdn.microsoft.com/en-us/library/ie/hh779016.aspx
          window.navigator.msSaveBlob(data.responseBody, data.filename);
        else {
          const linkElement = document.createElement("a");
          try {
            linkElement.setAttribute("href", data.url);
            linkElement.setAttribute("download", data.filename);

            const clickEvent = new MouseEvent("click", {
              view: window,
              bubbles: true,
              cancelable: false,
            });

            linkElement.dispatchEvent(clickEvent);
          } catch (ex) {
            console.log(ex);
          }
        }
      }),
      mapTo(undefined),
      catchError((error) => {
        this.messageService.error(error);
        return throwError(error);
      }))
  }

  private toFileData(response$: Observable<HttpResponse<Blob>>, inputData: GetFileInputData): Observable<GetFileData> {
    return response$.pipe(
      map((response) => this.buildFileData(response, inputData)),
      catchError((error) => {
        this.messageService.error(error);
        return EMPTY;
      })
    );
  }

  private buildFileData(response: HttpResponse<Blob>, inputData: GetFileInputData): GetFileData {
    // Get the filename from the content-disposition filename header or default to "download.bin"
    let filename = inputData.filename ? inputData.filename : "download.bin";
    try {
      const disposition = response.headers.get("content-disposition");
      if (disposition && disposition.indexOf("attachment") !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }
      let objectUrl: any = window.URL.createObjectURL(response.body);
      if (inputData.trustUrl) {
        objectUrl = this.domSanitizer.bypassSecurityTrustUrl(objectUrl);
      }
      return { responseBody: response.body, url: objectUrl, filename };
    } catch (e) { }
  }
}
